import React, { forwardRef, useImperativeHandle, useCallback, useState, useRef, useEffect } from 'react';
import { useTemplateEngine } from './useTemplateEngine';
import { CanvasStage } from './CanvasStage';
import { AssetSidebar } from './AssetSidebar';
import { EditorControls } from './EditorControls';
import { InlineTextEditor } from './InlineTextEditor';
import { Button } from '../ui/button';
import { X, Settings2, ChevronDown, Undo2, Redo2 } from 'lucide-react';

export const UserTemplateEditor = forwardRef(({
  template,
  onSave,
  onElementsChange,
  onPreviewUpdate,
  onNextStep,
  initialElements = [],
  initialBgColor = '#FFFFFF',
  availablePhotos = [],
  mainImageUrl = null,
  photoAdjustments = null,
  onPhotoAdjustmentsChange = null
}, ref) => {
  const engine = useTemplateEngine({
    template, onElementsChange, availablePhotos,
    initialElements, initialBgColor, photoAdjustments, onPhotoAdjustmentsChange
  });

  const [showPreview, setShowPreview] = useState(false);
  const [previewDataUrl, setPreviewDataUrl] = useState(null);
  const [mobileToolsOpen, setMobileToolsOpen] = useState(false);
  const canvasWrapperRef = useRef(null);

  // Auto-fit the canvas to the visible viewport whenever the wrapper size
  // changes (orientation, sidebar collapse, mobile rotation). Subtract some
  // padding so the design always shows fully on the screen.
  useEffect(() => {
    if (!canvasWrapperRef.current) return;
    const el = canvasWrapperRef.current;
    const compute = () => {
      const rect = el.getBoundingClientRect();
      const padding = 32;
      const availableW = Math.max(160, rect.width - padding);
      const availableH = Math.max(200, rect.height - padding);
      const sx = availableW / engine.canvasWidth;
      const sy = availableH / engine.canvasHeight;
      const next = Math.min(sx, sy, 1.5); // never zoom way past native
      if (next > 0 && Math.abs(next - engine.scale) > 0.01) {
        engine.setScale(next);
      }
    };
    compute();
    const ro = new ResizeObserver(compute);
    ro.observe(el);
    window.addEventListener('orientationchange', compute);
    return () => {
      ro.disconnect();
      window.removeEventListener('orientationchange', compute);
    };
    // We intentionally depend only on canvasWidth/Height; scale dep would loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [engine.canvasWidth, engine.canvasHeight]);

  const handlePreview = useCallback(() => {
    if (!engine.stageRef.current) return;
    const prevSelected = engine.selectedId;
    engine.setSelectedId(null);
    setTimeout(() => {
      // Guard against unmount during the 50ms wait (user may have navigated away)
      const stage = engine.stageRef.current;
      if (!stage) return;
      const dataUrl = stage.toDataURL({ pixelRatio: 2 });
      setPreviewDataUrl(dataUrl);
      setShowPreview(true);
      engine.setSelectedId(prevSelected);
    }, 50);
  }, [engine]);

  useImperativeHandle(ref, () => ({
    exportPreview: (highRes = false) => {
      if (!engine.stageRef.current) return null;
      const prev = engine.selectedId;
      engine.setSelectedId(null);
      return new Promise((resolve) => {
        setTimeout(() => {
          // Guard against unmount during the 50ms wait
          const stage = engine.stageRef.current;
          if (!stage) {
            resolve(null);
            return;
          }
          const dataUrl = stage.toDataURL({ pixelRatio: highRes ? 3 : 2 });
          engine.setSelectedId(prev);
          resolve(dataUrl);
        }, 50);
      });
    },
    getPreviewSync: () => {
      if (!engine.stageRef.current) return null;
      return engine.stageRef.current.toDataURL({ pixelRatio: 2 });
    },
    downloadDesign: engine.downloadDesign,
    getPhotoAdjustments: engine.getPhotoAdjustments
  }), [engine]);

  // Track mouse position for overlay hover (uses canvas coordinates)
  const getCanvasPos = useCallback((e) => {
    const container = engine.stageRef.current?.container();
    const rect = container ? container.getBoundingClientRect() : e.currentTarget.getBoundingClientRect();
    return {
      x: (e.clientX - rect.left) / engine.scale,
      y: (e.clientY - rect.top) / engine.scale
    };
  }, [engine.stageRef, engine.scale]);

  // Mobile: when a selection happens (e.g. user tapped a text or shape on the
  // canvas), auto-open the bottom-sheet tools so the user can edit immediately
  // without having to find the floating tools button.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (window.innerWidth >= 1024) return; // desktop has the sidebar always visible
    if (!engine.selectedId) return;
    if (engine.inlineEditingId) return; // user is inline-editing — don't fight
    // Photo placeholder tap is handled separately below — skip auto-open here
    // so we don't fight with the file-upload trigger.
    if (engine.selectedPhotoPlaceholder === engine.selectedId) return;
    setMobileToolsOpen(true);
  }, [engine.selectedId, engine.selectedPhotoPlaceholder, engine.inlineEditingId]);

  // Mobile: when a user taps an EMPTY photo placeholder (DIY mode with no
  // scraped photos) — directly trigger the file picker instead of forcing
  // them to dig through the bottom-sheet.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (window.innerWidth >= 1024) return;
    if (!engine.selectedPhotoPlaceholder) return;
    if (availablePhotos && availablePhotos.length > 0) return; // picker already shown
    const el = engine.elements.find(e => e.id === engine.selectedPhotoPlaceholder);
    if (!el) return;
    const isEmpty = !el.src || el.isPlaceholder || el.src.includes('placeholder');
    if (isEmpty && engine.fileInputRef?.current) {
      engine.fileInputRef.current.click();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [engine.selectedPhotoPlaceholder]);

  // Touch swipe handlers for multi-page navigation on mobile.
  // We attach these to the canvas wrapper *outside* the Konva stage so they
  // don't interfere with element drag/select inside the stage.
  const swipeStart = useRef({ x: 0, y: 0, t: 0 });
  const SWIPE_THRESHOLD_PX = 60;
  const SWIPE_MAX_VERTICAL = 50;
  const SWIPE_MAX_DURATION = 600;

  const handleTouchStart = useCallback((e) => {
    if (!engine.isMultiPage || engine.pages.length <= 1) return;
    if (e.touches.length !== 1) return;
    const t = e.touches[0];
    swipeStart.current = { x: t.clientX, y: t.clientY, t: Date.now() };
  }, [engine.isMultiPage, engine.pages.length]);

  const handleTouchEnd = useCallback((e) => {
    if (!engine.isMultiPage || engine.pages.length <= 1) return;
    const start = swipeStart.current;
    if (!start || !e.changedTouches.length) return;
    const t = e.changedTouches[0];
    const dx = t.clientX - start.x;
    const dy = t.clientY - start.y;
    const dt = Date.now() - start.t;
    if (dt > SWIPE_MAX_DURATION) return;
    if (Math.abs(dy) > SWIPE_MAX_VERTICAL) return;
    if (Math.abs(dx) < SWIPE_THRESHOLD_PX) return;
    const idx = engine.currentPageIndex;
    if (dx < 0 && idx < engine.pages.length - 1) {
      engine.switchPage(idx + 1);
    } else if (dx > 0 && idx > 0) {
      engine.switchPage(idx - 1);
    }
  }, [engine]);

  return (
    <div className="flex flex-col lg:flex-row gap-4 h-full" data-testid="template-editor">
      {/* Desktop sidebar */}
      <div className="hidden lg:block">
        <AssetSidebar
          engine={engine}
          template={template}
          availablePhotos={availablePhotos}
          onNextStep={onNextStep}
          onPreview={handlePreview}
        />
      </div>

      <div
        ref={canvasWrapperRef}
        className="flex-1 flex flex-col items-center justify-center bg-muted/20 rounded-lg p-2 sm:p-4 overflow-auto min-h-[400px] pb-24 lg:pb-4 relative"
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        {/* Undo / Redo floating chips — visible on both mobile and desktop */}
        <div className="absolute top-2 left-2 z-20 flex gap-1.5" data-testid="editor-history-toolbar">
          <button
            onClick={() => engine.undo()}
            className="h-9 w-9 inline-flex items-center justify-center rounded-full bg-white/90 backdrop-blur border border-gray-200 shadow-sm hover:bg-white hover:border-gray-400 active:scale-95 transition-all"
            title="Undo (Ctrl/Cmd+Z)"
            aria-label="Undo"
            data-testid="editor-undo-btn"
          >
            <Undo2 className="h-4 w-4 text-gray-700" />
          </button>
          <button
            onClick={() => engine.redo()}
            className="h-9 w-9 inline-flex items-center justify-center rounded-full bg-white/90 backdrop-blur border border-gray-200 shadow-sm hover:bg-white hover:border-gray-400 active:scale-95 transition-all"
            title="Redo (Ctrl/Cmd+Shift+Z)"
            aria-label="Redo"
            data-testid="editor-redo-btn"
          >
            <Redo2 className="h-4 w-4 text-gray-700" />
          </button>
        </div>
        {/* Multi-page navigation */}
        {engine.isMultiPage && engine.pages.length > 1 && (
          <>
            <div className="hidden sm:flex items-center gap-2 mb-3" data-testid="user-page-nav">
              {engine.pages.map((page, idx) => (
                <button
                  key={page.id || idx}
                  onClick={() => engine.switchPage(idx)}
                  className={`px-3 py-1.5 text-xs rounded-md font-medium transition-colors ${
                    engine.currentPageIndex === idx
                      ? 'bg-primary text-primary-foreground shadow-sm'
                      : 'bg-muted hover:bg-muted/80 text-muted-foreground'
                  }`}
                  data-testid={`user-page-btn-${idx}`}
                >
                  {page.label || `Page ${idx + 1}`}
                </button>
              ))}
              <span className="text-xs text-muted-foreground ml-2">
                {engine.currentPageIndex + 1} of {engine.pages.length}
              </span>
            </div>
            <div className="sm:hidden flex flex-col items-center gap-1.5 mb-3" data-testid="user-page-nav-mobile">
              <div className="flex items-center gap-1.5">
                {engine.pages.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => engine.switchPage(idx)}
                    className={`h-2 rounded-full transition-all ${
                      engine.currentPageIndex === idx ? 'bg-primary w-6' : 'bg-muted w-2'
                    }`}
                    aria-label={`Page ${idx + 1}`}
                    data-testid={`user-page-dot-${idx}`}
                  />
                ))}
              </div>
              <span className="text-[10px] text-muted-foreground tracking-wide uppercase">
                ← swipe · {engine.pages[engine.currentPageIndex]?.label || `Page ${engine.currentPageIndex + 1}`} · swipe →
              </span>
            </div>
          </>
        )}
        <div
          className="border-2 border-border rounded-lg shadow-xl relative"
          style={{
            width: engine.canvasWidth * engine.scale,
            height: engine.canvasHeight * engine.scale,
            backgroundColor: engine.bgColor,
            touchAction: 'none',
          }}
          onMouseMove={(e) => {
            const pos = getCanvasPos(e);
            engine.updateHoveredOverlay(pos.x, pos.y);
          }}
          onMouseLeave={() => engine.clearHoveredOverlay()}
          onDragOver={(e) => {
            e.preventDefault();
            e.dataTransfer.dropEffect = 'copy';
            const pos = getCanvasPos(e);
            engine.findDragOverSlot(pos.x, pos.y);
          }}
          onDragLeave={engine.clearDragOverSlot}
          onDrop={(e) => {
            e.preventDefault();
            const photoUrl = e.dataTransfer.getData('application/x-photo-url') || e.dataTransfer.getData('text/plain');
            if (photoUrl && photoUrl.startsWith('http')) {
              const pos = getCanvasPos(e);
              engine.handleDrop(photoUrl, pos.x, pos.y);
            }
          }}
          data-testid="canvas-drop-zone"
        >
          <CanvasStage engine={engine} />
          <EditorControls engine={engine} availablePhotos={availablePhotos} />
        </div>
        <InlineTextEditor engine={engine} canvasWrapperRef={canvasWrapperRef} />
      </div>

      <input
        ref={engine.fileInputRef}
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        onChange={engine.handleImageUpload}
      />

      {/* Full-screen Preview Modal */}
      {showPreview && previewDataUrl && (
        <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center"
          onClick={() => setShowPreview(false)} data-testid="preview-modal">
          <Button variant="ghost" size="icon"
            className="absolute top-4 right-4 text-white hover:bg-white/20 z-10"
            onClick={() => setShowPreview(false)} data-testid="preview-close-button">
            <X className="h-6 w-6" />
          </Button>
          <div className="max-w-[90vw] max-h-[90vh] flex items-center justify-center"
            onClick={(e) => e.stopPropagation()}>
            <img src={previewDataUrl} alt="Design Preview"
              className="max-w-full max-h-[90vh] object-contain rounded-lg shadow-2xl"
              data-testid="preview-image" />
          </div>
          <p className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white/60 text-sm">
            Click anywhere or press X to close
          </p>
        </div>
      )}

      {/* Mobile floating "Tools" button (opens bottom sheet) */}
      <button
        type="button"
        onClick={() => setMobileToolsOpen(true)}
        className="lg:hidden fixed bottom-20 right-4 z-40 h-14 w-14 rounded-full bg-primary text-primary-foreground shadow-xl flex items-center justify-center active:scale-95 transition-transform"
        aria-label="Open editing tools"
        data-testid="mobile-tools-fab"
      >
        <Settings2 className="h-6 w-6" />
      </button>

      {/* Mobile bottom-sheet AssetSidebar */}
      {mobileToolsOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex flex-col" data-testid="mobile-tools-sheet">
          <div className="flex-1 bg-black/40" onClick={() => setMobileToolsOpen(false)} />
          <div className="bg-white rounded-t-2xl shadow-2xl max-h-[80vh] flex flex-col">
            <div className="flex items-center justify-between p-3 border-b border-gray-200 sticky top-0 bg-white rounded-t-2xl">
              <div className="flex items-center gap-2">
                <Settings2 className="h-4 w-4 text-gray-500" />
                <span className="font-semibold text-sm">Edit Tools</span>
              </div>
              <button
                onClick={() => setMobileToolsOpen(false)}
                className="p-2 -mr-2 rounded-md hover:bg-gray-100"
                aria-label="Close"
                data-testid="mobile-tools-close"
              >
                <ChevronDown className="h-5 w-5" />
              </button>
            </div>
            <div className="overflow-y-auto flex-1">
              <AssetSidebar
                engine={engine}
                template={template}
                availablePhotos={availablePhotos}
                onNextStep={() => { setMobileToolsOpen(false); onNextStep && onNextStep(); }}
                onPreview={() => { setMobileToolsOpen(false); handlePreview(); }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
});
